import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	inject,
	Input,
	NgZone,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	fromEvent,
	Observable,
	Subject,
	tap,
} from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
	FilterState,
	ProjectDisplayMode,
	ProjectViewMode,
} from '../../domain/catalog-item.types';
import { CatalogFilterService } from './catalog-filter.service';

type ResultKind = 'projects' | 'folders';

interface BarVm {
	filterState: FilterState;
	selectedTags: Array<string>;
	hasActiveFilters: boolean;
}

interface DropdownVm {
	open: boolean;
	tags: Array<string>;
	query: string;
	top: number;
	left: number;
}

@Component({
	selector: 'app-catalog-filter-bar',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ['./catalog-filter-bar.component.scss'],
	templateUrl: './catalog-filter-bar.component.html',
	providers: [CatalogFilterService],
})
export class CatalogFilterBarComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	private readonly elRef = inject(ElementRef);
	private readonly zone = inject(NgZone);
	private barResizeObserver?: ResizeObserver;
	private barMutationObserver?: MutationObserver;
	private readonly destroy$ = new Subject<void>();
	@ViewChild('kwTrigger')
	private readonly kwTrigger!: ElementRef<HTMLButtonElement>;
	public readonly svc = inject(CatalogFilterService);

	@Output()
	public filteringState = new EventEmitter<FilterState>();

	// The grouping-tag dock owns that selection, so the bar's own trigger and
	// chips stay off screen. Everything behind them is wired: set true to show
	// the control, for plain tags or anything else.
	public readonly tagFilterVisible = false;

	// Same for the folder view. The mode works end to end; only the way into it
	// is hidden.
	public readonly folderViewVisible = false;

	// --- Dropdown state ---
	private readonly dropdownOpen$ = new BehaviorSubject(false);
	private readonly dropdownPos$ = new BehaviorSubject<{
		top: number;
		left: number;
	}>({
		top: 0,
		left: 0,
	});
	public readonly typeaheadQuery$ = new BehaviorSubject('');
	private readonly availableTagsSubject = new BehaviorSubject<Array<string>>(
		[]
	);

	// --- View models ---
	public readonly barVm$: Observable<BarVm> = combineLatest([
		this.svc.filterState$,
		this.svc.hasActiveFilters$,
	]).pipe(
		map(([filterState, hasActiveFilters]) => ({
			filterState,
			selectedTags: [...filterState.keywords],
			hasActiveFilters,
		}))
	);

	public readonly dropdownVm$: Observable<DropdownVm> = combineLatest([
		this.dropdownOpen$,
		this.typeaheadQuery$,
		this.availableTagsSubject,
		this.svc.filterState$,
		this.dropdownPos$,
	]).pipe(
		map(([open, query, available, state, pos]) => {
			const q = query.toLowerCase();
			const selected = state.keywords;
			const tags = available
				.filter((t) => !selected.has(t))
				// Typeahead matches what the option reads as, not the raw value:
				// the untagged bucket's value is a sentinel, not a tag code.
				.filter((t) => !q || this.tagLabel(t).toLowerCase().includes(q));
			return {
				open,
				query,
				tags,
				top: pos.top,
				left: pos.left,
			};
		})
	);

	// Counts are pushed in by the host: the AngularJS projects page owns the
	// collection and filters it, so the bar never sees the items.
	private readonly resultCountSubject = new BehaviorSubject(0);
	private readonly totalCountSubject = new BehaviorSubject(0);
	private readonly resultKindSubject = new BehaviorSubject<ResultKind>(
		'projects'
	);

	public readonly resultsVm$: Observable<{
		filtered: number;
		total: number;
		kind: ResultKind;
	}> = combineLatest([
		this.resultCountSubject,
		this.totalCountSubject,
		this.resultKindSubject,
	]).pipe(
		map(([filtered, total, kind]) => ({
			filtered,
			total,
			kind,
		}))
	);

	// --- Input / Output ---
	private _availableTags: Array<string> = [];

	public get availableTags(): Array<string> {
		return this._availableTags;
	}

	@Input()
	public set availableTags(tags: Array<string>) {
		this._availableTags = tags;
		this.availableTagsSubject.next(tags);
	}

	// Seeding only: the host (AngularJS projects page) owns the persisted value and
	// pushes it in once. The value is published back out through `filteringState`.
	@Input()
	public set viewMode(mode: ProjectViewMode) {
		this.svc.setViewMode(mode);
	}

	// Seeded the same way as the view mode: the host restores it from local
	// storage and pushes it in once.
	@Input()
	public set displayMode(mode: ProjectDisplayMode) {
		this.svc.setDisplayMode(mode);
	}

	// Display labels for tag values that are sentinels rather than real codes -
	// today just the "untagged" bucket, whose wording the host localizes.
	private _tagLabels: Record<string, string> = {};

	@Input()
	public set tagLabels(labels: Record<string, string>) {
		this._tagLabels = labels ? labels : {};
	}

	public tagLabel(tag: string): string {
		return this._tagLabels[tag] ? this._tagLabels[tag] : tag;
	}

	@Input()
	public set resultCount(count: number) {
		this.resultCountSubject.next(count ? count : 0);
	}

	@Input()
	public set totalCount(count: number) {
		this.totalCountSubject.next(count ? count : 0);
	}

	// What is being counted: folders while the folder view lists them, projects
	// everywhere else (card view, and a folder drilled into).
	@Input()
	public set resultKind(kind: ResultKind) {
		this.resultKindSubject.next(kind === 'folders' ? 'folders' : 'projects');
	}

	// --- Handlers ---
	// The dock is either on screen or it is not; 'cards' is what off is called.
	// The host persists whichever lands, so the choice survives a reload.
	public toggleDock(current: ProjectViewMode): void {
		this.svc.setViewMode(current === 'groups' ? 'cards' : 'groups');
	}

	public toggleDropdown(event: MouseEvent): void {
		event.stopPropagation();
		const next = !this.dropdownOpen$.value;
		if (next) {
			const rect = this.kwTrigger.nativeElement.getBoundingClientRect();
			this.dropdownPos$.next({
				top: rect.bottom + 6,
				left: rect.left,
			});
		} else {
			this.typeaheadQuery$.next('');
		}
		this.dropdownOpen$.next(next);
	}

	public onDropdownPanelClick(event: MouseEvent): void {
		event.stopPropagation();
	}

	public selectTag(tag: string, event: MouseEvent): void {
		event.stopPropagation();
		this.svc.toggleTag(tag);
		this.typeaheadQuery$.next('');
	}

	public removeTag(tag: string, event: MouseEvent): void {
		event.stopPropagation();
		this.svc.toggleTag(tag);
	}

	public setSearch(val: string): void {
		this.svc.setSearch(val);
	}

	// Any click that reaches the document closes the dropdown, including one
	// elsewhere in the bar. The panel, its options and the trigger all stop
	// propagation, so a click on the dropdown itself never gets here - testing
	// the host element instead would keep it open for the rest of the bar.
	@HostListener('document:click')
	public onDocumentClick(): void {
		this.closeDropdown();
	}

	public ngOnInit(): void {
		// capture:true catches scroll events on any nested scrollable container,
		// not just the window — needed because overflow:auto on .bar creates a
		// scroll ancestor that window:scroll would miss
		fromEvent(document, 'scroll', {
			capture: true,
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe((event) => {
				// ignore scrolls inside the dropdown's own tag list
				if (!this.elRef.nativeElement.contains(event.target as Node)) {
					this.closeDropdown();
				}
			});

		// filterChanges$, not filterState$: the host owns the persisted view and
		// display modes and seeds them through @Input, so only what the user did
		// goes back out. The service's opening defaults are nobody's choice.
		this.svc.filterChanges$
			.pipe(
				takeUntil(this.destroy$),
				tap((state) => this.filteringState.emit(state))
			)
			.subscribe();
	}

	public ngAfterViewInit(): void {
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const host = this.elRef.nativeElement as HTMLElement;
		this.zone.runOutsideAngular(() => {
			// The host, not .bar: .bar sits inside an *ngIf driven by an async
			// pipe, so it may not exist yet at this point. The host always does.
			this.barResizeObserver = new ResizeObserver(() =>
				this.syncSeparators()
			);
			this.barResizeObserver.observe(host);

			// Covers both .bar appearing for the first time and sections being
			// added to it later, neither of which has to change the host's size.
			this.barMutationObserver = new MutationObserver(() =>
				this.syncSeparators()
			);
			this.barMutationObserver.observe(host, {
				childList: true,
				subtree: true,
			});
		});
		this.syncSeparators();
	}

	public ngOnDestroy(): void {
		this.barResizeObserver?.disconnect();
		this.barMutationObserver?.disconnect();
		this.destroy$.next();
		this.destroy$.complete();
	}

	// A separator that lands first or last on a wrapped line reads as a rule
	// hanging off the edge. visibility rather than display: the box keeps its
	// size, so hiding one cannot change where the next line breaks.
	private syncSeparators(): void {
		const bar = (this.elRef.nativeElement as HTMLElement).querySelector(
			'.bar'
		);
		if (!bar) {
			return;
		}
		this.publishHeight();
		const children = Array.from(bar.children) as Array<HTMLElement>;
		const rects = children.map((child) => child.getBoundingClientRect());
		children.forEach((child, index) => {
			if (!child.classList.contains('sep')) {
				return;
			}
			const startsLine = !this.onSameLine(rects[index - 1], rects[index]);
			const endsLine = !this.onSameLine(rects[index], rects[index + 1]);
			child.classList.toggle('sep--hidden', startsLine || endsLine);
		});
	}

	// The bar is sticky, so anything that wants to sit below it needs its
	// height - and that height changes as the controls wrap. Published as a
	// custom property rather than a hardcoded number.
	private publishHeight(): void {
		const host = this.elRef.nativeElement as HTMLElement;
		document.documentElement.style.setProperty(
			'--dc-filter-bar-h',
			`${host.getBoundingClientRect().height}px`
		);
	}

	// The separator is shorter than the controls beside it and vertically
	// centred, so its top never matches theirs. Two boxes share a line when
	// they overlap vertically at all.
	private onSameLine(a?: DOMRect, b?: DOMRect): boolean {
		if (!a || !b) {
			return false;
		}
		return a.top < b.bottom && b.top < a.bottom;
	}

	@HostListener('window:resize')
	public onResize(): void {
		this.closeDropdown();
	}

	private closeDropdown(): void {
		if (this.dropdownOpen$.value) {
			this.dropdownOpen$.next(false);
			this.typeaheadQuery$.next('');
		}
	}
}
